import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Clock, Sparkles, MapPin, HeartHandshake, MessageSquare, X, ChevronRight, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import MatchingGate from "@/components/app/MatchingGate";
import { IconAvatar } from "@/components/app/IconAvatar";
import { getLikedProfiles, likeBackProfile, passLikedProfile } from "@/lib/api";
import { getProfile } from "@/lib/storage";

export default function Liked() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("received");
  const [liked, setLiked] = useState({ received: [], sent: [], matchCount: 0 });
  const [loading, setLoading] = useState(true);
  const soloMode = getProfile()?.usageMode === "solo";

  const loadLiked = async () => {
    if (soloMode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setLiked(await getLikedProfiles());
    } catch (error) {
      toast("Could not load likes.", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLiked(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const received = liked.received || [];
  const sent = liked.sent || [];

  const likeBack = async (candidate) => {
    try {
      await likeBackProfile(candidate.userId);
      toast(`It's a match with ${candidate.profile.fullName.split(" ")[0]}.`, {
        description: "Both of you said yes. Say hi in Chat or start a partnership from Matches.",
      });
      navigate("/matches");
    } catch (error) {
      toast("Could not confirm this match.", { description: error.message });
    }
  };

  const dismiss = async (candidate) => {
    try {
      await passLikedProfile(candidate.userId);
      setLiked((current) => ({ ...current, received: current.received.filter((item) => item.userId !== candidate.userId) }));
      toast("Passed.");
    } catch (error) {
      toast("Could not update this like.", { description: error.message });
    }
  };

  return (
    <AppShell>
      <MatchingGate>
      <div className="px-6 sm:px-10 py-10 max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="overline mb-3">Liked</div>
          <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
            The waiting room — <em className="italic text-[#F59E0B]">where matches form.</em>
          </h1>
          <p className="mt-3 text-[#A1A1AA] font-light max-w-2xl">
            A like isn't a match. Only when both sides say yes do you land in Matches. Review who's already
            said yes to you — a like back is the confirmation.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/5 mb-6" data-testid="liked-tabs">
          <Tab active={tab === "received"} onClick={() => setTab("received")} count={received.length} testId="liked-tab-received">
            <HeartHandshake className="w-3.5 h-3.5" strokeWidth={1.5} /> Liked you
          </Tab>
          <Tab active={tab === "sent"} onClick={() => setTab("sent")} count={sent.length} testId="liked-tab-sent">
            <Heart className="w-3.5 h-3.5" strokeWidth={1.5} /> You liked
          </Tab>
        </div>

        {loading && <div className="text-sm text-[#A1A1AA] py-10 text-center">Loading likes...</div>}

        {!loading && tab === "received" && (
          <>
            {received.length === 0 ? (
              <Empty
                icon={HeartHandshake}
                title="No one's waiting for your yes."
                body="When someone likes you, they'll appear here. Meanwhile, keep browsing in Discover — the more real signals you send, the more come back."
                cta={{ to: "/discover", label: "Go to Discover" }}
              />
            ) : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="liked-received-grid">
                {received.map((candidate) => {
                  const p = candidate.profile;
                  return <div key={candidate.userId} className="hm-card p-6 rounded-md" data-testid={`liked-received-${candidate.userId}`}>
                    <div className="flex items-start gap-4 mb-4">
                      <IconAvatar iconKey={p.profileIcon} size="lg" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-serif-editorial text-xl leading-tight truncate">{p.fullName}</h3>
                          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30">
                            Liked you
                          </span>
                        </div>
                        <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mt-1">
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.timezone}</span>
                        </div>
                        <p className="mt-3 text-sm text-[#F5F5F0] font-light line-clamp-2">{p.goalDescription}</p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {p.goalTags.slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                      <Link to={`/profile/${candidate.userId}`} data-testid={`liked-view-${candidate.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs">
                        <UserIcon className="w-3.5 h-3.5" /> Profile
                      </Link>
                      <button
                        onClick={() => dismiss(candidate)}
                        data-testid={`liked-dismiss-${candidate.userId}`}
                        className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs"
                        title="Not interested"
                      >
                        <X className="w-3.5 h-3.5" /> Pass
                      </button>
                      <button
                        onClick={() => likeBack(candidate)}
                        data-testid={`liked-back-${candidate.userId}`}
                        className="hm-btn-primary ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
                      >
                        <HeartHandshake className="w-3.5 h-3.5" /> Like back
                      </button>
                    </div>
                  </div>;
                })}
              </div>
            )}
          </>
        )}

        {!loading && tab === "sent" && (
          <>
            {sent.length === 0 ? (
              <Empty
                icon={Heart}
                title="You haven't liked anyone yet."
                body="Head to Discover and send some intentional likes. Skipping is silent, but liking is a signal — use it deliberately."
                cta={{ to: "/discover", label: "Browse candidates" }}
              />
            ) : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="liked-sent-grid">
                {sent.map((candidate) => {
                  const p = candidate.profile;
                  return <div key={candidate.userId} className="hm-card p-6 rounded-md" data-testid={`liked-sent-${candidate.userId}`}>
                    <div className="flex items-start gap-4">
                      <IconAvatar iconKey={p.profileIcon} size="lg" tone="outline" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-serif-editorial text-xl leading-tight truncate">{p.fullName}</h3>
                          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-white/10 text-[#A1A1AA]">
                            Awaiting
                          </span>
                        </div>
                        <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mb-3">
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.timezone}</span>
                        </div>
                        <p className="text-sm text-[#F5F5F0] font-light line-clamp-2 mb-3">{p.goalDescription}</p>
                        <div className="flex items-center gap-2">
                          <Link to={`/profile/${candidate.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs">
                            <UserIcon className="w-3.5 h-3.5" /> Profile
                          </Link>
                          <div className="ml-auto text-[10px] text-[#A1A1AA] uppercase tracking-[0.2em] flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                            Waiting for their yes
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
                })}
              </div>
            )}
          </>
        )}

        {!loading && liked.matchCount > 0 && (
          <div className="mt-10 p-5 rounded-md border border-white/5 bg-white/[0.02] flex items-center gap-4">
            <Sparkles className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-sm text-[#F5F5F0]">You already have {liked.matchCount} confirmed {liked.matchCount === 1 ? "match" : "matches"}.</div>
              <div className="text-xs text-[#A1A1AA]">A match happens when both sides say yes.</div>
            </div>
            <Link to="/matches" className="hm-btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs">
              See matches <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>
      </MatchingGate>
    </AppShell>
  );
}

function Tab({ active, onClick, count, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-[#F59E0B] text-[#F5F5F0]" : "border-transparent text-[#A1A1AA] hover:text-[#F5F5F0]"
      }`}
    >
      {children}
      {count > 0 && (
        <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${active ? "bg-[#F59E0B] text-black" : "border border-white/10 text-[#A1A1AA]"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function Empty({ icon: Icon, title, body, cta }) {
  return (
    <div className="hm-card p-10 rounded-md text-center max-w-lg mx-auto">
      <Icon className="w-8 h-8 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.25} />
      <h2 className="font-serif-editorial text-2xl mb-3">{title}</h2>
      <p className="text-[#A1A1AA] font-light mb-6">{body}</p>
      {cta && (
        <Link to={cta.to} className="hm-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold">
          {cta.label} <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}
